import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Truck,
  Mail,
  Phone,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SortableTableHeader from '../components/SortableTableHeader';
import BalanceTotalsBar from '../components/transactions/BalanceTotalsBar';
import PurchaseTransactionSummaryCards, {
  type PurchaseSummaryCardStatus,
} from '../components/PurchaseTransactionSummaryCards';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../utils/supplierApi';
import { getPurchaseTransactions } from '../utils/purchaseTransactionApi';
import {
  encodeSupplierSort,
  loadSuppliersTablePreferences,
  parseSupplierSort,
  saveSuppliersTablePreferences,
  type SortDir,
} from '../utils/suppliersTablePreferences';
import { ApiValidationError } from '../types/auth';
import type { Supplier, CreateSupplierData, UpdateSupplierData } from '../types/supplier';
import type { PaymentTerms } from '../types/customer';
import type { PurchaseTransactionSummary } from '../types/purchaseTransaction';
import type { PaginationMeta } from '../types/journalEntry';
import { toast, toastApiError } from '../utils/toast';
import { computeSupplierTotals, supplierRowKey } from '../utils/supplierSelection';

const PAYMENT_TERMS: PaymentTerms[] = ['IMMEDIATE', 'NET_30', 'NET_60', 'NET_90'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const SEARCH_DEBOUNCE_MS = 400;

type ServerSortKey = 'display_name' | 'email' | 'payment_terms' | 'balance' | 'active';

const SERVER_SORT_KEYS = new Set<ServerSortKey>([
  'display_name',
  'email',
  'payment_terms',
  'balance',
  'active',
]);

const isServerSortable = (column: string): column is ServerSortKey =>
  SERVER_SORT_KEYS.has(column as ServerSortKey);

const defaultSortDir = (key: ServerSortKey): SortDir => (key === 'balance' ? 'desc' : 'asc');

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const canEditSupplier = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteSupplier = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const defaultEditFormData = (): UpdateSupplierData => ({
  display_name: '',
  email: '',
  phone: '',
  address: '',
  payment_terms: 'NET_30',
  active: true,
});

const defaultCreateFormData = (): CreateSupplierData => ({
  display_name: '',
  email: '',
  phone: '',
  address: '',
  payment_terms: 'NET_30',
  active: true,
});

const Suppliers = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [termsFilter, setTermsFilter] = useState<'all' | PaymentTerms>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [balanceFilter, setBalanceFilter] = useState<'all' | 'with_balance' | 'zero_balance'>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => loadSuppliersTablePreferences().perPage);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState<UpdateSupplierData>(defaultEditFormData());
  const [createFormData, setCreateFormData] = useState<CreateSupplierData>(defaultCreateFormData());
  const [editError, setEditError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [submitting, setSubmitting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [purchaseSummary, setPurchaseSummary] = useState<PurchaseTransactionSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<ServerSortKey>(() => {
    const parsed = parseSupplierSort(loadSuppliersTablePreferences().sort);
    if (parsed && isServerSortable(parsed.field)) {
      return parsed.field;
    }
    return 'display_name';
  });
  const [sortDir, setSortDir] = useState<SortDir>(() => {
    const parsed = parseSupplierSort(loadSuppliersTablePreferences().sort);
    if (parsed && isServerSortable(parsed.field)) {
      return parsed.direction;
    }
    return 'asc';
  });
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const showActionsColumn = canEditSupplier() || canDeleteSupplier();

  useEffect(() => {
    saveSuppliersTablePreferences({
      sort: encodeSupplierSort(sortKey, sortDir),
      perPage,
    });
  }, [sortKey, sortDir, perPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchPurchaseSummary = async () => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const result = await getPurchaseTransactions({
        type: 'bill',
        paid_days: 30,
        per_page: 1,
        page: 1,
      });
      setPurchaseSummary(result.summary);
    } catch (err) {
      setSummaryError(err instanceof Error ? err.message : 'Failed to fetch purchase summary');
      setPurchaseSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseSummary();
  }, []);

  const fetchSuppliers = async (pageNum = page) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getSuppliers({
        search: searchQuery || undefined,
        payment_terms: termsFilter === 'all' ? undefined : termsFilter,
        active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        has_balance:
          balanceFilter === 'all'
            ? undefined
            : balanceFilter === 'with_balance',
        sort: encodeSupplierSort(sortKey, sortDir),
        per_page: perPage,
        page: pageNum,
      });
      setSuppliers(result.suppliers);
      setMeta(result.meta);
      setPage(result.meta.current_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch vendors');
      setSuppliers([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, termsFilter, statusFilter, balanceFilter, perPage, sortKey, sortDir]);

  const openCreateModal = () => {
    setCreateFormData(defaultCreateFormData());
    setCreateError(null);
    setCreateFieldErrors({});
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateFormData(defaultCreateFormData());
    setCreateError(null);
    setCreateFieldErrors({});
  };

  const handleCreateSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setCreateFieldErrors({});

    if (!createFormData.display_name?.trim()) {
      setCreateFieldErrors({ display_name: ['Display name is required'] });
      return;
    }

    try {
      setCreating(true);
      await createSupplier({
        display_name: createFormData.display_name.trim(),
        email: createFormData.email?.trim() || null,
        phone: createFormData.phone?.trim() || null,
        address: createFormData.address?.trim() || null,
        payment_terms: createFormData.payment_terms,
        active: createFormData.active,
      });
      closeCreateModal();
      await Promise.all([fetchSuppliers(1), fetchPurchaseSummary()]);
      toast.success('Vendor created successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setCreateFieldErrors(err.errors);
        setCreateError(err.message);
      } else {
        toastApiError(err, 'Failed to create vendor');
      }
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      display_name: supplier.display_name,
      email: supplier.email ?? '',
      phone: supplier.phone ?? '',
      address: supplier.address ?? '',
      payment_terms: supplier.payment_terms,
      active: supplier.active,
    });
    setEditError(null);
    setFieldErrors({});
    setShowEditModal(true);
  };

  const handleUpdateSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSupplier) return;

    setEditError(null);
    setFieldErrors({});

    try {
      setSubmitting(true);
      await updateSupplier(selectedSupplier.id, {
        display_name: formData.display_name?.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        address: formData.address?.trim() || null,
        payment_terms: formData.payment_terms,
        active: formData.active,
      });
      setShowEditModal(false);
      setSelectedSupplier(null);
      await Promise.all([fetchSuppliers(page), fetchPurchaseSummary()]);
      toast.success('Vendor updated successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setEditError(err.message);
      } else {
        toastApiError(err, 'Failed to update vendor');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSupplier = async (id: number) => {
    if (!canDeleteSupplier()) {
      toast.error('Only administrators can delete vendors.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await deleteSupplier(id);
      await Promise.all([fetchSuppliers(page), fetchPurchaseSummary()]);
      toast.success('Vendor deleted successfully!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete vendor');
    }
  };

  const hasActiveFilters =
    searchQuery || termsFilter !== 'all' || statusFilter !== 'all' || balanceFilter !== 'all';

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setTermsFilter('all');
    setStatusFilter('all');
    setBalanceFilter('all');
    setPage(1);
  };

  const handleSupplierSelect = (supplier: Supplier) => {
    navigate(`/purchase-transactions?vendor=${supplier.id}`);
  };

  const handleSummaryCardClick = (status: PurchaseSummaryCardStatus) => {
    if (status === 'open' || status === 'overdue') {
      setBalanceFilter('with_balance');
      setPage(1);
      return;
    }

    navigate('/purchases', { state: { statusFilter: status } });
  };

  const handleSort = (column: ServerSortKey) => {
    if (sortKey === column) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(column);
      setSortDir(defaultSortDir(column));
    }
  };

  const fieldError = (field: string) => fieldErrors[field]?.[0];
  const createFieldError = (field: string) => createFieldErrors[field]?.[0];

  const pageTotals = useMemo(() => computeSupplierTotals(suppliers), [suppliers]);

  const visibleRowKeys = useMemo(
    () => suppliers.map(supplierRowKey),
    [suppliers]
  );

  const selectedRows = useMemo(
    () => suppliers.filter((s) => selectedKeys.has(supplierRowKey(s))),
    [suppliers, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computeSupplierTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [searchQuery, termsFilter, statusFilter, balanceFilter, page, perPage]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData = totalsBarMode === 'selected' ? selectedTotals : pageTotals;
  const totalsBarPageSuffix =
    meta && meta.total > pageTotals.count ? ' on this page' : '';
  const showTotalsBar = suppliers.length > 0;

  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
      <Sidebar />
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 lg:p-6 relative">
        <div className="flex flex-1 flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-full">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Suppliers</h1>
                <p className="mt-1 text-sm text-gray-500">Accounts payable vendor list</p>
              </div>
              {canEditSupplier() && (
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Supplier
                </button>
              )}
            </div>
          </div>

          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Purchasing summary</p>
            {summaryError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-red-800 flex-1">{summaryError}</p>
                <button type="button" onClick={fetchPurchaseSummary} className="text-red-700 underline shrink-0">
                  Retry
                </button>
              </div>
            )}
            <PurchaseTransactionSummaryCards
              summary={purchaseSummary}
              loading={summaryLoading}
              countLabel="bill"
              onCardClick={handleSummaryCardClick}
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => fetchSuppliers(page)} className="underline shrink-0">
                Retry
              </button>
            </div>
          )}

          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="lg:col-span-2">
                  <label htmlFor="supplier-search" className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      id="supplier-search"
                      type="text"
                      placeholder="Name, email, or phone..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-slate-800 focus:border-slate-600"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="termsFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <select
                    id="termsFilter"
                    value={termsFilter}
                    onChange={(e) => {
                      setTermsFilter(e.target.value as 'all' | PaymentTerms);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Terms</option>
                    {PAYMENT_TERMS.map((term) => (
                      <option key={term} value={term}>
                        {formatLabel(term)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="balanceFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Balance
                  </label>
                  <select
                    id="balanceFilter"
                    value={balanceFilter}
                    onChange={(e) => {
                      setBalanceFilter(e.target.value as 'all' | 'with_balance' | 'zero_balance');
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Balances</option>
                    <option value="with_balance">With Balance</option>
                    <option value="zero_balance">Zero Balance</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="perPage" className="block text-sm font-medium text-gray-700 mb-1">
                    Per Page
                  </label>
                  <select
                    id="perPage"
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <p className="text-sm text-gray-500">
                {meta?.total ?? 0} {meta?.total === 1 ? 'vendor' : 'vendors'}
              </p>
              {hasActiveFilters && (
                <>
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Filters applied</span>
                  <button type="button" onClick={clearFilters} className="text-sm text-slate-700 underline">
                    Clear filters
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
              </div>
            ) : (
              <table className="w-full min-w-full border-separate border-spacing-0">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-800"
                        aria-label="Select all on this page"
                      />
                    </th>
                    <SortableTableHeader label="Vendor" column="display_name" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Contact" column="email" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Terms" column="payment_terms" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Balance" column="balance" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortableTableHeader label="Status" column="active" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    {showActionsColumn && (
                      <th className="px-4 py-3 text-right text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide sticky top-0 z-20 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {suppliers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={showActionsColumn ? 7 : 6}
                        className="px-6 py-12 text-center"
                      >
                        <Truck className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">No vendors found</p>
                        {hasActiveFilters && (
                          <button
                            type="button"
                            onClick={clearFilters}
                            className="mt-4 text-sm text-slate-700 underline"
                          >
                            Clear filters
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    suppliers.map((supplier) => {
                      const rowKey = supplierRowKey(supplier);
                      const isCheckboxSelected = selectedKeys.has(rowKey);
                      return (
                      <tr
                        key={rowKey}
                        className={`hover:bg-gray-50 cursor-pointer${
                          isCheckboxSelected ? ' bg-slate-50/60' : ''
                        }`}
                        onClick={() => handleSupplierSelect(supplier)}
                      >
                        <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isCheckboxSelected}
                            onChange={(e) => toggleRow(rowKey, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-slate-800"
                            aria-label={`Select ${supplier.display_name}`}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center">
                              <Truck className="h-4 w-4 text-slate-700" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{supplier.display_name}</div>
                              {supplier.address && (
                                <div className="text-xs text-gray-500 truncate max-w-xs">{supplier.address}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-1 text-gray-900">
                            <Mail className="h-3.5 w-3.5 text-gray-400" />
                            {supplier.email || '—'}
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                            <Phone className="h-3.5 w-3.5 text-gray-400" />
                            {supplier.phone || '—'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{formatLabel(supplier.payment_terms)}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {parseFloat(supplier.balance).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${supplier.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}
                          >
                            {supplier.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        {(canEditSupplier() || canDeleteSupplier()) && (
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              {canEditSupplier() && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(supplier);
                                  }}
                                  className="text-slate-600 hover:text-slate-900"
                                  title="Edit vendor"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              {canDeleteSupplier() && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSupplier(supplier.id);
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                  title="Delete vendor"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {meta && meta.last_page > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-sm text-gray-500">
                Page {meta.current_page} of {meta.last_page} · {meta.total} total
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fetchSuppliers(meta.current_page - 1)}
                  disabled={meta.current_page <= 1 || loading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => fetchSuppliers(meta.current_page + 1)}
                  disabled={meta.current_page >= meta.last_page || loading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
          </div>

          {showTotalsBar && !loading && (
            <BalanceTotalsBar
              mode={totalsBarMode}
              count={totalsBarData.count}
              primaryLabel="Balance"
              primaryAmount={totalsBarData.balance}
              pageCountSuffix={totalsBarPageSuffix}
              onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
            />
          )}
        </div>
      </main>

      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-semibold">New Supplier</h3>
                <p className="text-sm text-gray-500">Create a vendor in your company</p>
              </div>
              <button type="button" onClick={closeCreateModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateSupplier} className="p-6 space-y-4">
              {createError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{createError}</span>
                </div>
              )}
              <div>
                <label htmlFor="create-display_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <input
                  id="create-display_name"
                  type="text"
                  required
                  maxLength={200}
                  value={createFormData.display_name}
                  onChange={(e) => setCreateFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${createFieldError('display_name') ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="Acme Suppliers"
                />
                {createFieldError('display_name') && (
                  <p className="mt-1 text-sm text-red-600">{createFieldError('display_name')}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="create-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="create-email"
                    type="email"
                    maxLength={150}
                    value={createFormData.email ?? ''}
                    onChange={(e) => setCreateFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${createFieldError('email') ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="orders@acme.com"
                  />
                  {createFieldError('email') && (
                    <p className="mt-1 text-sm text-red-600">{createFieldError('email')}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="create-phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    id="create-phone"
                    type="tel"
                    maxLength={30}
                    value={createFormData.phone ?? ''}
                    onChange={(e) => setCreateFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${createFieldError('phone') ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="+94771234567"
                  />
                  {createFieldError('phone') && (
                    <p className="mt-1 text-sm text-red-600">{createFieldError('phone')}</p>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="create-address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  id="create-address"
                  rows={2}
                  value={createFormData.address ?? ''}
                  onChange={(e) => setCreateFormData((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="456 Industrial Rd"
                />
              </div>
              <div>
                <label htmlFor="create-payment_terms" className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Terms
                </label>
                <select
                  id="create-payment_terms"
                  value={createFormData.payment_terms}
                  onChange={(e) =>
                    setCreateFormData((prev) => ({ ...prev, payment_terms: e.target.value as PaymentTerms }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {PAYMENT_TERMS.map((term) => (
                    <option key={term} value={term}>
                      {formatLabel(term)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <input
                  id="create-active"
                  type="checkbox"
                  checked={createFormData.active ?? true}
                  onChange={(e) => setCreateFormData((prev) => ({ ...prev, active: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="create-active" className="ml-2 text-sm text-gray-700">
                  Active vendor
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
                >
                  {creating ? 'Creating...' : 'Create Supplier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Edit Supplier</h3>
              <button type="button" onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateSupplier} className="p-6 space-y-4">
              {editError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{editError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                <input
                  type="text"
                  required
                  maxLength={200}
                  value={formData.display_name ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${fieldError('display_name') ? 'border-red-300' : 'border-gray-300'}`}
                />
                {fieldError('display_name') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('display_name')}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email ?? ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone ?? ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  rows={2}
                  value={formData.address ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                <select
                  value={formData.payment_terms}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, payment_terms: e.target.value as PaymentTerms }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {PAYMENT_TERMS.map((term) => (
                    <option key={term} value={term}>
                      {formatLabel(term)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <input
                  id="edit-active"
                  type="checkbox"
                  checked={formData.active ?? true}
                  onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="edit-active" className="ml-2 text-sm text-gray-700">
                  Active vendor
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Suppliers;
